/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useRef, useState } from "react";

import { Button, classnames, Icon, ModalDialog, PlainButton } from "@culturehq/components";

import { useDoor } from "app/hooks";
import { errorService, getDuration, Trans } from "app/utils";
import PlaybackGrabberFrame from "./PlaybackGrabberFrame";
import ChqModalDialog from "./common/ChqModalDialog";

const SelectVideoFrame = ({
  initialOpen = false,
  story,
  thumbnail,
  onFrameSelected,
  onPauseParent,
  preview
}) => {
  const videoRef = useRef();
  const cursorMarkerRef = useRef();
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(1);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoUrl, setVideoUrl] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [seekValue, setSeekValue] = useState(0);
  const [loading, setLoading] = useState(false);

  const { open, onOpen: onOpenModal, onClose } = useDoor(initialOpen);

  useEffect(
    () => {
      if (story.media.url) {
        setVideoUrl(story.media.url);
      // if it is a local file. Ex. Blob {size: 1717311, type: "video/mp4"}
      } else if (story.media.type?.startsWith("video/")) {
        setVideoUrl(URL.createObjectURL(story.media));
      }
    }, [story]
  );

  const onOpen = () => {
    if (onPauseParent) onPauseParent();
    onOpenModal();
  };

  const handleVideoClick = () => {
    if (Number.isFinite(videoDuration)) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const changeVolume = () => {
    if (videoRef.current.volume) {
      setVolumeLevel(0);
      videoRef.current.volume = 0;
    } else {
      setVolumeLevel(1);
      videoRef.current.volume = 1;
    }
  };

  const handleCursorMarkerRefUpdateLeft = value => {
    cursorMarkerRef.current.style.left = value;
  };

  const handleVideoRefUpdateTime = value => {
    videoRef.current.currentTime = value;
  };

  const captureFrame = (video, currentTime, canvas, ctx) => new Promise(resolve => {
    // eslint-disable-next-line no-param-reassign
    video.currentTime = currentTime;
    video.addEventListener("seeked", async function handleSeeked() {
      video.removeEventListener("seeked", handleSeeked);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      // Using toBlob instead of toDataURL
      canvas.toBlob(blob => {
        if (blob) {
          resolve(blob); // return the Blob instead of a DataURL
        } else {
          resolve(null);
        }
      }, "image/png");
    });
  });

  const handleSelectFrame = async () => {
    setLoading(true);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx || !videoRef.current) {
      errorService.log("Canvas context or videoRef not available");
      setLoading(false);
      return;
    }
    // Fitting canvas size to video
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    try {
      const thumbnail = await captureFrame(videoRef.current, currentTime, canvas, ctx);
      onFrameSelected(thumbnail);
    } catch (error) {
      errorService.log("Error capturing frame");
    }
    setLoading(false);
    onClose();
  };

  return (
    <>
      {open && (
        <ChqModalDialog entrance="zoomIn" onClose={onClose} className="trim-video-modal" ariaLabelledBy="trim-video-title" ariaDescribedby="trim-video-title" width="normal">
          <ModalDialog.Heading bigCloseIcon onClose={onClose}>
            <span><h3 className="trim-video-title" id="trim-video-title"><Trans>Select frame for video</Trans></h3></span>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <div className="gallery-lightbox__photo">
              <div className="gallery-lightbox__image-container" style={{ backgroundImage: `url(${thumbnail})` }} />
              <div className={classnames("Video-Container", { "Video-Container__Paused": !videoPlaying })}>
                <video
                  ref={videoRef}
                  autoPlay
                  className="Video-Container__Video"
                  onPause={() => setVideoPlaying(false)}
                  onPlay={() => setVideoPlaying(true)}
                  poster={thumbnail}
                  crossOrigin="anonymous"
                  src={videoUrl}
                  onTimeUpdate={e => {
                    if (Number.isFinite(videoDuration)) {
                      handleCursorMarkerRefUpdateLeft(`${(e.target.currentTime / videoRef.current.duration) * 100}%`);
                    }
                    setSeekValue((e.target.currentTime * 100) / videoRef.current.duration);
                    const current = e.target.currentTime;
                    setCurrentTime(current < 0 || current > videoDuration ? 0 : current);
                    if (videoDuration === Infinity && Number.isFinite(videoRef.current.duration)) {
                      setVideoDuration(videoRef.current.duration);
                    }
                  }}
                  onLoadedData={() => {
                    setVideoDuration(videoRef.current.duration);
                  }}
                  onClick={handleVideoClick}
                />
                <div className="Video--Controls">
                  <div className="Video--Controls_Container">
                    <div className="Video--Controls-Play">
                      {videoPlaying ? (
                        <PlainButton
                          className="Btn-Play"
                          onClick={handleVideoClick}
                        >
                          <Icon icon="pause-no-bg" />
                        </PlainButton>
                      ) : (
                        <PlainButton
                          className="Btn-Play"
                          onClick={handleVideoClick}
                        >
                          <Icon icon="play-no-border" />
                        </PlainButton>
                      )}
                      <span className="Video-Duration">{getDuration(currentTime)}/{getDuration(videoDuration)}</span>
                    </div>
                    <div className="Video--Controls-Utilities">
                      <PlainButton
                        className="Volume-Btn"
                        onClick={changeVolume}
                      >
                        {volumeLevel === 0 ? <Icon icon="speaker-muted" /> : <Icon icon="speaker" />}
                      </PlainButton>
                    </div>
                  </div>
                  <input
                    className="Seek-Bar"
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={seekValue}
                    onChange={e => {
                      const seekto = videoRef.current.duration * (+e.target.value / 100);
                      handleVideoRefUpdateTime(seekto);
                      setSeekValue(e.target.value);
                      const current = seekto;
                      setCurrentTime(current < 0 || current > videoDuration ? 0 : current);
                    }}
                  />
                </div>
              </div>
            </div>
            <PlaybackGrabberFrame
              cursorMarkerRef={cursorMarkerRef}
              onVideoRefUpdateTime={handleVideoRefUpdateTime}
              videoRef={videoRef}
              videoDuration={videoDuration}
              videoUrl={videoUrl}
              preview={preview}
            />
          </ModalDialog.Body>
          <ModalDialog.Footer>
            {!Number.isFinite(videoDuration) && (
              <span>
                <Trans>Please wait a few moments while your thumbnails are generated</Trans>
              </span>
            )}
            <Button disabled={!Number.isFinite(videoDuration)} onClick={handleSelectFrame}>
              <Trans>Save Thumbnail</Trans>
            </Button>
          </ModalDialog.Footer>
        </ChqModalDialog>
      )}
      <div className="TrimVideo-Container-Thumbnail" onClick={onOpen}>
        <PlainButton className="TrimVideo-Btn" onClick={onOpen}>
          <Icon className="camera" icon="video-marketing" />
          <Trans>Select Frame</Trans>
        </PlainButton>
      </div>
    </>
  );
};

export default SelectVideoFrame;
